import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { publicApi } from "@/lib/api";

const SESSION_KEY = "lpl_popup_dismissed";

export default function SitePopup() {
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith("/admin");

    const [popup, setPopup] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (isAdmin) return undefined;
        let alive = true;
        publicApi
            .getPopup()
            .then((data) => {
                if (!alive) return;
                if (
                    data &&
                    data.active &&
                    (data.title || data.content) &&
                    !sessionStorage.getItem(SESSION_KEY)
                ) {
                    setPopup(data);
                    setOpen(true);
                }
            })
            .catch(() => {
                /* silencieux : si l'API ne répond pas, on ne montre rien */
            });
        return () => {
            alive = false;
        };
    }, [isAdmin]);

    const close = useCallback(() => {
        sessionStorage.setItem(SESSION_KEY, "1");
        setOpen(false);
    }, []);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, close]);

    if (!open || !popup) return null;

    return (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-8 bg-lpl-text/70 backdrop-blur-sm"
            onClick={close}
            data-testid="site-popup"
        >
            <div
                className="relative w-full max-w-lg bg-lpl-bg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* offset decorative block */}
                <div
                    aria-hidden
                    className="absolute -bottom-4 -right-4 w-full h-full bg-lpl-primary-deep -z-10"
                />
                {/* yellow tape */}
                <div
                    aria-hidden
                    className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-lpl-yellow/80 -rotate-[5deg] shadow-sm"
                />

                <button
                    type="button"
                    onClick={close}
                    aria-label="Fermer"
                    data-testid="site-popup-close"
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-lpl-primary text-lpl-text flex items-center justify-center hover:bg-lpl-text hover:text-lpl-bg transition-colors"
                >
                    <X size={18} />
                </button>

                <div className="px-8 md:px-10 py-10 md:py-12">
                    <p className="font-serif italic text-base text-lpl-terracotta mb-2">
                        Une info importante
                    </p>
                    {popup.title && (
                        <h2
                            className="font-display font-semibold text-3xl md:text-4xl text-lpl-text leading-[1.05] tracking-tight mb-4"
                            data-testid="site-popup-title"
                        >
                            {popup.title}
                        </h2>
                    )}
                    {popup.content && (
                        <p
                            className="text-base md:text-lg text-lpl-text-muted leading-relaxed whitespace-pre-line"
                            data-testid="site-popup-content"
                        >
                            {popup.content}
                        </p>
                    )}

                    <button
                        type="button"
                        onClick={close}
                        className="mt-8 inline-flex items-center gap-2 bg-lpl-primary text-lpl-text px-6 py-3 rounded-full text-xs uppercase tracking-[0.18em] font-semibold hover:bg-lpl-text hover:text-lpl-bg transition-colors"
                        data-testid="site-popup-ok"
                    >
                        J'ai compris
                    </button>
                </div>
            </div>
        </div>
    );
}
